import { createBody } from './body.js';
import { createPerception } from './perception.js';

const MIN_GAP = 250;
const CALL_CAP = 1000;
const APPEAR_DELAY = 5000;
const FAIL_LIMIT = 3;
const DORMANT_MS = 30000;
const REQUEST_TIMEOUT = 8000;

const INTERRUPT_REASONS = new Set(['click', 'hover', 'select', 'copy']);

export function nextBeatGate(gate) {
  if (gate.performing && gate.interrupt) {
    return { performing: false, resting: false, accept: true, interrupt: true };
  }
  if (gate.performing) {
    return { performing: true, resting: gate.resting, accept: false, interrupt: false };
  }
  if (gate.resting) {
    return { performing: false, resting: false, accept: true, interrupt: false };
  }
  return { performing: false, resting: false, accept: true, interrupt: false };
}

function circleAround(rect) {
  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;
  const radius = Math.max(42, Math.min(78, Math.max(rect.width, rect.height) * 0.55));
  const points = [];
  for (let index = 0; index <= 36; index += 1) {
    const angle = (index / 36) * Math.PI * 2;
    points.push({
      x: cx + Math.cos(angle) * radius,
      y: cy + Math.sin(angle) * radius,
    });
  }
  return points;
}

function timeoutSignal(ms) {
  if (typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function') {
    return AbortSignal.timeout(ms);
  }
  const controller = new AbortController();
  window.setTimeout(() => controller.abort(), ms);
  return controller.signal;
}

function circlePoints(origin) {
  const start = performance.now();
  const points = [];
  for (let index = 0; index <= 36; index += 1) {
    const angle = (index / 36) * Math.PI * 2;
    points.push({
      x: origin.x + Math.cos(angle) * 54,
      y: origin.y + Math.sin(angle) * 54,
      t: start + index * 16,
    });
  }
  return points;
}

function wigglePoints(origin) {
  const start = performance.now();
  const points = [];
  for (let index = 0; index < 12; index += 1) {
    points.push({
      x: origin.x + (index % 2 === 0 ? -50 : 50),
      y: origin.y,
      t: start + index * 40,
    });
  }
  return points;
}

export function createMind({ layer, debug, blocked, onStatus }) {
  const body = createBody(layer);
  let perception;
  let firstActivity = 0;
  let appeared = false;
  let warmed = false;
  let givenUp = false;
  let inFlight = false;
  let queued = null;
  let gapTimer = null;
  let appearTimer = null;
  let pauseTimer = null;
  let lastSentAt = 0;
  let calls = 0;
  let failures = 0;
  let dormantUntil = 0;
  let ignoredStreak = 0;
  let openGuess = null;
  let lastPredictionFailed = 'unknown';
  let inviteUsed = false;
  let lastPlayful = false;
  let isBlocked = blocked;
  let lastPointer = { x: window.innerWidth / 2, y: window.innerHeight / 2 };
  let performing = false;
  let resting = false;
  let motionFollow = null;
  let signaled = false;
  let signalWaiting = false;
  let signalTimer = null;
  let epoch = 0;
  const session = [];

  perception = createPerception({
    onActivity: onActivity,
    onBeat: onBeat,
    onConfetti: (from) => body.confetti(from),
    getBloopPosition: () => body.position(),
  });
  body.setOnIdle(onMotionEnd);

  function onMotionEnd() {
    if (motionFollow) {
      const follow = motionFollow;
      motionFollow = null;
      follow();
      return;
    }
    performing = false;
    if (signalWaiting) trySignal();
  }

  function runSignal(node) {
    const line = node.querySelector('[data-bloop-id="project_bloop_line"]');
    const title = node.querySelector('[data-bloop-id="project_bloop"]') || node;
    performing = true;
    resting = true;
    queued = null;
    motionFollow = () => {
      body.play({
        verb: 'imitate_gesture',
        feeling: 'excited',
        certainty: 0.84,
        args: {
          size: 'same',
          speed: 'same',
          repetitions: 'once',
          mirror: false,
          keepPlace: true,
        },
      }, {
        path: circleAround(title.getBoundingClientRect()),
        visitorAction: { type: 'move' },
      });
      if (!body.isMoving()) performing = false;
    };
    if (line) {
      body.play({
        verb: 'imitate_on_element',
        feeling: 'excited',
        certainty: 0.8,
        args: { target: 'project_bloop_line' },
      }, { visitorAction: { type: 'select', element_id: 'project_bloop_line' } });
    }
    if (!body.isMoving()) {
      const follow = motionFollow;
      motionFollow = null;
      if (follow) follow();
    }
  }

  function trySignal() {
    if (signaled) return false;
    const node = document.querySelector('[data-bloop-signal]');
    if (!node) return false;
    const rect = node.getBoundingClientRect();
    const onScreen = rect.bottom > 80 && rect.top < window.innerHeight * 0.9;
    if (!onScreen) return false;
    if (!appeared || performing) {
      signalWaiting = true;
      return false;
    }
    signaled = true;
    signalWaiting = false;
    runSignal(node);
    return true;
  }

  function lookForSignal() {
    if (signaled) return;
    window.requestAnimationFrame(() => trySignal());
  }

  function onPageScroll() {
    if (signaled) return;
    window.clearTimeout(signalTimer);
    signalTimer = window.setTimeout(() => trySignal(), 450);
  }

  function publish(extra) {
    if (!debug || !onStatus) return;
    onStatus({
      calls,
      failures,
      givenUp,
      appeared,
      ...extra,
    });
  }

  function remember(entry) {
    if (!debug) return;
    session.push(entry);
    if (session.length > 80) session.shift();
  }

  function restartPauseTimer() {
    window.clearTimeout(pauseTimer);
    pauseTimer = window.setTimeout(() => {
      if (!appeared || !lastPlayful || inviteUsed || givenUp || isBlocked || document.hidden) return;
      enqueue(capture('pause'));
    }, 2000);
  }

  function onActivity(sample) {
    lastPointer = { x: sample.x, y: sample.y };
    body.setPointer(sample);
    if (perception.isTyping()) {
      body.freeze(true);
      return;
    }
    body.freeze(false);
    if (isBlocked || document.hidden) return;
    inviteUsed = false;
    restartPauseTimer();
    if (!firstActivity) {
      firstActivity = performance.now();
      if (debug && !appeared) {
        appeared = true;
        body.showAt(sample.x + 160, sample.y - 50);
      }
      enqueue(capture('warmup'));
    }
    maybeAppear();
  }

  function onBeat(reason) {
    if (isBlocked || document.hidden) return;
    const gate = nextBeatGate({
      performing,
      resting,
      interrupt: INTERRUPT_REASONS.has(reason),
    });
    if (gate.interrupt) {
      epoch += 1;
      motionFollow = null;
      signalWaiting = false;
      queued = null;
      body.hold();
    }
    performing = gate.performing;
    resting = gate.resting;
    if (!gate.accept) return;
    const item = capture(reason);
    publish({ observation: item.snapshot, decision: null, ms: null, reason });
    enqueue(item);
  }

  function capture(reason) {
    return {
      reason,
      snapshot: perception.snapshot(),
      path: perception.lastPath(),
      epoch,
    };
  }

  function maybeAppear() {
    if (appeared || givenUp || !warmed || !firstActivity || appearTimer || debug) return;
    const wait = Math.max(0, APPEAR_DELAY - (performance.now() - firstActivity));
    appearTimer = window.setTimeout(() => {
      appearTimer = null;
      if (appeared || givenUp || !warmed || document.hidden || isBlocked) return;
      appeared = true;
      body.enter(lastPointer);
      trySignal();
    }, wait);
  }

  function enqueue(item) {
    if (givenUp || isBlocked || document.hidden || perception.isTyping()) return;
    if (item.reason !== 'warmup' && performance.now() < dormantUntil) return;
    if (calls >= CALL_CAP) return;
    queued = item;
    if (inFlight) return;
    const wait = MIN_GAP - (performance.now() - lastSentAt);
    if (lastSentAt && wait > 0) {
      if (gapTimer) return;
      gapTimer = window.setTimeout(() => {
        gapTimer = null;
        const next = queued;
        queued = null;
        if (next) dispatch(next);
      }, wait);
      return;
    }
    const next = queued;
    queued = null;
    dispatch(next);
  }

  function settleGuess(snapshot) {
    if (!openGuess) return;
    if (openGuess.kind === 'element' && snapshot.visitor_action && snapshot.visitor_action.element_id) {
      lastPredictionFailed = snapshot.visitor_action.element_id === openGuess.id ? 'no' : 'yes';
      openGuess = null;
    } else if (openGuess.kind === 'gesture' && snapshot.current_gesture) {
      const same = snapshot.current_gesture.turning === openGuess.turning
        && snapshot.current_gesture.reversals === openGuess.reversals;
      lastPredictionFailed = same ? 'no' : 'yes';
      openGuess = null;
    }
  }

  function rememberGuess(decision, item) {
    if (decision.verb !== 'anticipate' && decision.verb !== 'exaggerate') {
      openGuess = null;
      return;
    }
    if (decision.verb === 'anticipate' && decision.args.next_element) {
      openGuess = { kind: 'element', id: decision.args.next_element };
      return;
    }
    if (item.snapshot.current_gesture) {
      openGuess = {
        kind: 'gesture',
        turning: item.snapshot.current_gesture.turning,
        reversals: item.snapshot.current_gesture.reversals,
      };
    }
  }

  function apply(decision, item) {
    const response = decision.labels && decision.labels.response;
    if (response === 'ignored') ignoredStreak += 1;
    else if (response && response !== 'unclear') ignoredStreak = 0;
    if (ignoredStreak >= 3) {
      ignoredStreak = 0;
      dormantUntil = performance.now() + DORMANT_MS;
      motionFollow = null;
      body.hold();
      return;
    }
    if (decision.verb === 'invite' && (inviteUsed || !lastPlayful)) {
      body.hold();
      return;
    }
    if (decision.verb === 'invite') inviteUsed = true;
    if (decision.glyph && performance.now() - apply.lastGlyph < 10000) decision.glyph = null;
    if (decision.glyph) apply.lastGlyph = performance.now();
    const note = body.play(decision, {
      path: item.path,
      snapshot: item.snapshot,
      visitorAction: item.snapshot.visitor_action,
      gesture: Boolean(item.snapshot.current_gesture),
    });
    if (body.isMoving()) {
      performing = true;
      resting = true;
      queued = null;
    }
    if (note) perception.note(note);
    if (['imitate_gesture', 'imitate_on_element', 'anticipate', 'exaggerate', 'flee'].includes(decision.verb)) {
      lastPlayful = true;
    }
    rememberGuess(decision, item);
  }
  apply.lastGlyph = 0;

  async function dispatch(item) {
    if (givenUp || calls >= CALL_CAP) return;
    settleGuess(item.snapshot);
    item.snapshot.last_prediction_failed = lastPredictionFailed;
    item.snapshot.debug = debug;
    inFlight = true;
    calls += 1;
    lastSentAt = performance.now();
    const started = performance.now();
    try {
      const response = await fetch('/api/bloop', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(item.snapshot),
        signal: timeoutSignal(REQUEST_TIMEOUT),
      });
      if (!response.ok) throw new Error(String(response.status));
      const decision = await response.json();
      failures = 0;
      const ms = Math.round(performance.now() - started);
      warmed = true;
      const current = item.epoch === epoch;
      if (current && item.reason !== 'warmup' && appeared && !performing) apply(decision, item);
      else if (current && (item.reason === 'warmup' || !appeared)) maybeAppear();
      remember({ t: Date.now(), reason: item.reason, ms, observation: item.snapshot, decision });
      publish({ observation: item.snapshot, decision, ms, reason: item.reason });
    } catch (error) {
      failures += 1;
      remember({ t: Date.now(), reason: item.reason, error: error.name || 'error' });
      publish({ observation: item.snapshot, decision: null, ms: null, reason: item.reason, error: error.name || 'error' });
      if (failures >= FAIL_LIMIT) {
        givenUp = true;
        if (appeared && !debug) body.leave();
      }
    } finally {
      inFlight = false;
      if (queued && !givenUp && !performing) {
        const next = queued;
        queued = null;
        enqueue(next);
      } else if (performing) queued = null;
    }
  }

  function playVerb(verb) {
    const place = body.position();
    const target = [...document.querySelectorAll('[data-bloop-id]')].find((node) => {
      const rect = node.getBoundingClientRect();
      return rect.width > 2 && rect.bottom > 0 && rect.top < window.innerHeight && rect.right > 0 && rect.left < window.innerWidth;
    });
    const id = target ? target.getAttribute('data-bloop-id') : null;
    const path = circlePoints({ x: place.x || lastPointer.x, y: place.y || lastPointer.y });
    appeared = true;
    body.show();
    body.play({
      verb,
      feeling: verb === 'flee' ? 'startled' : 'curious',
      certainty: 0.72,
      glyph: verb === 'flee' ? '!' : null,
      args: {
        size: verb === 'exaggerate' ? 'much_bigger' : 'same',
        speed: verb === 'anticipate' ? 'faster' : 'same',
        repetitions: verb === 'exaggerate' ? 'several' : 'twice',
        mirror: false,
        flee_distance: 'across_the_screen',
        target: id,
        next_element: id,
      },
    }, {
      path,
      snapshot: { current_gesture: { turning: 'a full turn or more', reversals: 'none' } },
      visitorAction: { type: 'click', element_id: null },
      gesture: verb !== 'imitate_on_element',
    });
    if (body.isMoving()) {
      performing = true;
      resting = true;
      queued = null;
    }
  }

  function onVisibility() {
    if (document.hidden) body.pause();
    else body.resume();
  }

  return {
    start() {
      window.addEventListener('visibilitychange', onVisibility);
      window.addEventListener('scroll', onPageScroll, { passive: true });
      if (isBlocked) return;
      perception.start();
      if (debug) {
        appeared = true;
        body.showAt(window.innerWidth * 0.62, window.innerHeight * 0.38);
      }
      lookForSignal();
    },
    stop() {
      window.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('scroll', onPageScroll);
      perception.stop();
      window.clearTimeout(gapTimer);
      window.clearTimeout(appearTimer);
      window.clearTimeout(pauseTimer);
      window.clearTimeout(signalTimer);
      body.destroy();
    },
    setBlocked(next) {
      isBlocked = next;
      if (next) {
        perception.stop();
        body.hide();
        return;
      }
      perception.start();
      lookForSignal();
      if (debug && !appeared) {
        appeared = true;
        body.showAt(window.innerWidth * 0.62, window.innerHeight * 0.38);
        return;
      }
      if (appeared) body.show();
    },
    playVerb,
    injectCircle() {
      perception.inject(circlePoints(lastPointer));
    },
    injectWiggle() {
      perception.inject(wigglePoints(lastPointer));
    },
    session() { return session.slice(); },
    lookForSignal,
  };
}
